import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Fragment, useCallback, useMemo, useRef, useState } from 'react';

import Styles from './index.module.scss';
import { Chart } from './utils';

import type { ResponseRatioItem } from '@/app/types/Dashboard';
import type { Point } from 'highcharts';
import type { HighchartsReactRefObject } from 'highcharts-react-official';

interface Props {
	data: ResponseRatioItem;
}

const PieChartResponseRatio = ({ data }: Props) => {
	const chartRef = useRef<HighchartsReactRefObject>(null);
	const [points, setPoints] = useState<Point[]>([]);

	const options = useMemo(() => {
		return Chart.toResponseRatioOptions(data);
	}, [data]);

	const syncPointsFromChart = useCallback((chart?: Highcharts.Chart) => {
		if (!chart) {
			setPoints([]);
			return;
		}

		setPoints([...(chart.series[0]?.data ?? [])] as Point[]);
	}, []);

	const optionsWithEvents = useMemo<Highcharts.Options>(() => {
		return {
			...(options as Highcharts.Options),
			chart: {
				...options.chart,
				events: {
					...options.chart?.events,
					load(event) {
						syncPointsFromChart(this);
						options.chart?.events?.load?.call(this, event);
					},
					redraw(event) {
						syncPointsFromChart(this);
						options.chart?.events?.redraw?.call(this, event);
					},
				},
			},
		};
	}, [options, syncPointsFromChart]);

	return (
		<div className={`${Styles.container} ${!data ? Styles.empty : ''}`}>
			<div className={Styles.legend}>
				{points.map((point, index) => {
					return (
						<Fragment key={index}>
							<span className={Styles.title}>{point.name}</span>
							<div
								className={Styles.count}
								style={{ background: `${point.color as string}` }}
							>
								{`${point.y ?? 0}%`}
							</div>
						</Fragment>
					);
				})}
			</div>
			<div>
				<HighchartsReact
					options={optionsWithEvents}
					ref={chartRef}
					highcharts={Highcharts}
				/>
			</div>
		</div>
	);
};

export default PieChartResponseRatio;
